// Phase 5: real payment flow.
//   Step 1 — choose flow (text-to-pay vs in-app card)
//   Step 2 — amount + description
//   Step 3 — patient consent + e-signature (drawn or typed)
//   Step 4 — text-to-pay: phone capture + send SMS / in-app: Stripe Elements
//   Step 5 — receipt + status
//
// Stripe Elements is loaded via the official @stripe/stripe-js + react-stripe-js
// libraries (PCI SAQ-A scope: card data never touches our server).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from '../lib/api.js';
import { useBranding } from '../lib/branding.jsx';
import SignaturePad from '../components/SignaturePad.jsx';
import { useTenantHref } from '../lib/tenantPath.jsx';

const DEFAULT_CONSENT = `I authorize the pharmacy to charge the listed amount to the payment method I provide for the prescription medications dispensed for me. I acknowledge that the medications have been delivered or will be delivered to me, and that I have received counseling on these medications as appropriate. I confirm that this authorization is being made by me on the date and time captured below.`;

export default function Payment() {
  const tHref = useTenantHref();
  const { patientId } = useParams();
  const { branding } = useBranding();
  const nav = useNavigate();

  const [step, setStep] = useState(1);
  const [flow, setFlow] = useState('in_app');                // in_app | text2pay
  const [amount, setAmount] = useState('');                  // string in dollars
  const [description, setDescription] = useState('Prescription copay');

  const [consentText, setConsentText] = useState(DEFAULT_CONSENT);
  const [consentMethod, setConsentMethod] = useState('drawn'); // drawn | typed
  const [typedName, setTypedName] = useState('');
  const [signaturePng, setSignaturePng] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const sigRef = useRef(null);

  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [created, setCreated] = useState(null);            // server payment record
  const [stripeContext, setStripeContext] = useState(null); // { clientSecret, publishableKey }

  const amountCents = Math.round(Number(amount) * 100);
  const validAmount = amountCents >= 50;
  const consentReady = !!agreed && (consentMethod === 'drawn'
    ? !!signaturePng
    : typedName.trim().length >= 3);

  function next() { setErr(null); setStep(s => s + 1); }
  function back() { setErr(null); setStep(s => Math.max(1, s - 1)); }

  // Step 4 entry: kick off the right backend call
  async function startPayment() {
    setBusy(true); setErr(null);
    try {
      if (flow === 'in_app') {
        const r = await api.payInApp({ patient_id: patientId, amount_cents: amountCents, description });
        setCreated(r.payment);
        if (!r.publishable_key) throw new Error('Stripe publishable key not configured on the server');
        setStripeContext({ clientSecret: r.client_secret, publishableKey: r.publishable_key });
        // Submit the consent now that we have a payment.id
        await api.payConsent(r.payment.id, consentPayload());
      } else {
        const r = await api.payText2Pay({ patient_id: patientId, amount_cents: amountCents, description, phone });
        setCreated({ ...r.payment, payment_url: r.payment_url, sms_error: r.sms_error });
        await api.payConsent(r.payment.id, consentPayload());
      }
      next();
    } catch (e) {
      setErr(e.body?.message || e.message || 'Failed to start payment');
    } finally { setBusy(false); }
  }

  function consentPayload() {
    return {
      method: consentMethod,
      consent_text: consentText,
      typed_name: consentMethod === 'typed' ? typedName.trim() : null,
      signature_image: consentMethod === 'drawn' ? signaturePng : null,
    };
  }

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Take Payment</h1>
          <div className="text-sm text-slate-500">Patient ID: <span className="font-mono">{patientId}</span></div>
        </div>
        <Link to={tHref(`/patients/${patientId}`)} className="text-sm hover:underline" style={{ color: 'var(--brand-primary)' }}>
          ← Back to patient
        </Link>
      </div>

      <Stepper current={step} steps={['Method','Amount','Consent', flow === 'in_app' ? 'Card' : 'Send SMS','Receipt']} />

      {err && <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">{err}</div>}

      {step === 1 && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">How will the patient pay?</div>
          <FlowOption selected={flow === 'in_app'} onClick={() => setFlow('in_app')}
            title="Card on this device"
            body="Patient enters card details (or uses Apple Pay / Google Pay) on this screen. Funds settle to the facility immediately." />
          <FlowOption selected={flow === 'text2pay'} onClick={() => setFlow('text2pay')}
            title="Text-to-pay link"
            body="We SMS a secure payment link to the patient's phone. They open it and pay on their own device with Apple Pay, Google Pay, or any card." />
          <div className="flex gap-2">
            <button className="btn-primary" onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Amount</div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Amount (USD)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <input type="number" min="0.50" step="0.01" className="input pl-7 text-lg font-mono"
                value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div className="text-xs text-slate-500 mt-1">Minimum $0.50.</div>
          </div>
          <label className="block">
            <span className="text-xs text-slate-600">Description on receipt</span>
            <input className="input" value={description} onChange={e => setDescription(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={back}>Back</button>
            <button className="btn-primary" disabled={!validAmount} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card p-5 space-y-4">
          <div className="font-medium">Patient consent &amp; acknowledgment</div>
          <textarea
            className="input font-normal text-sm leading-relaxed"
            rows={6}
            value={consentText}
            onChange={e => setConsentText(e.target.value)}
          />
          <div className="text-xs text-slate-500">Edit if your facility uses different consent language. The exact text is hashed and audit-logged with the signature.</div>

          <div className="flex gap-2">
            <button className={`btn ${consentMethod === 'drawn' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setConsentMethod('drawn')}>Sign on screen</button>
            <button className={`btn ${consentMethod === 'typed' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setConsentMethod('typed')}>Type full name</button>
          </div>

          {consentMethod === 'drawn' && (
            <SignaturePad ref={sigRef} onChange={setSignaturePng} />
          )}
          {consentMethod === 'typed' && (
            <label className="block">
              <span className="text-xs text-slate-600">Patient's full legal name</span>
              <input className="input" value={typedName} onChange={e => setTypedName(e.target.value)} />
            </label>
          )}

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-1" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
            <span>I confirm the patient has read the above and authorizes the charge.</span>
          </label>

          <div className="flex gap-2">
            <button className="btn-ghost" onClick={back}>Back</button>
            <button className="btn-primary" disabled={!consentReady || busy} onClick={() => { setStep(4); }}>
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 4 && flow === 'text2pay' && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Send pay link to patient's phone</div>
          <label className="block">
            <span className="text-xs text-slate-600">Mobile number</span>
            <input type="tel" className="input font-mono" placeholder="+1 555 555 5555"
              value={phone} onChange={e => setPhone(e.target.value)} />
            <span className="text-xs text-slate-500">Twilio test mode requires the recipient be a verified number on your Twilio account.</span>
          </label>
          <div className="text-sm bg-slate-50 rounded p-3">
            <div className="text-xs text-slate-500 mb-1">SMS preview</div>
            <div className="font-mono text-xs whitespace-pre-wrap">
              {`${branding.display_name || 'Pharmacy'}: pay $${(amountCents/100 || 0).toFixed(2)} for your prescription securely: <link>`}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={back} disabled={busy}>Back</button>
            <button className="btn-primary" disabled={!phone || busy} onClick={startPayment}>
              {busy ? 'Sending…' : 'Send SMS'}
            </button>
          </div>
        </div>
      )}

      {step === 4 && flow === 'in_app' && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Card details</div>
          {!stripeContext && (
            <button className="btn-primary" disabled={busy} onClick={startPayment}>
              {busy ? 'Preparing…' : 'Initialize secure card form'}
            </button>
          )}
          {stripeContext && (
            <StripeCardForm
              clientSecret={stripeContext.clientSecret}
              publishableKey={stripeContext.publishableKey}
              amountCents={amountCents}
              onSuccess={() => { next(); }}
              onError={(m) => setErr(m)}
            />
          )}
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={back} disabled={busy}>Back</button>
          </div>
        </div>
      )}

      {step === 5 && (
        <Receipt payment={created} flow={flow} onDone={() => nav(`/patients/${patientId}`)} />
      )}
    </div>
  );
}

function Stepper({ current, steps }) {
  return (
    <ol className="flex flex-wrap gap-2 text-xs">
      {steps.map((s, i) => {
        const idx = i + 1; const done = idx < current; const active = idx === current;
        return (
          <li key={s} className={`px-2 py-1 rounded ${done ? 'bg-emerald-100 text-emerald-800'
            : active ? 'text-white' : 'bg-slate-100 text-slate-600'}`}
            style={active ? { background: 'var(--brand-primary)' } : undefined}>
            {idx}. {s}
          </li>
        );
      })}
    </ol>
  );
}

function FlowOption({ selected, onClick, title, body }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full text-left card p-4 hover:bg-slate-50 ${selected ? 'ring-2' : ''}`}
      style={selected ? { '--tw-ring-color': 'var(--brand-primary)' } : undefined}>
      <div className="font-medium" style={{ color: selected ? 'var(--brand-primary)' : undefined }}>{title}</div>
      <div className="text-sm text-slate-600">{body}</div>
    </button>
  );
}

function StripeCardForm({ clientSecret, publishableKey, amountCents, onSuccess, onError }) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <StripeCardFormInner amountCents={amountCents} onSuccess={onSuccess} onError={onError} />
    </Elements>
  );
}

function StripeCardFormInner({ amountCents, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    setSubmitting(false);
    if (error) { onError?.(error.message); return; }
    if (paymentIntent?.status === 'succeeded') onSuccess?.(paymentIntent);
    else onError?.(`Payment status: ${paymentIntent?.status || 'unknown'}`);
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <PaymentElement options={{ layout: 'accordion' }} />
      <button type="submit" disabled={!stripe || submitting} className="btn-primary w-full">
        {submitting ? 'Processing…' : `Pay $${(amountCents/100).toFixed(2)}`}
      </button>
      <div className="text-xs text-slate-500 text-center">
        Apple Pay / Google Pay show automatically when supported. Card data is sent directly to Stripe (PCI SAQ-A).
      </div>
    </form>
  );
}

function Receipt({ payment, flow, onDone }) {
  const [poll, setPoll] = useState(payment);
  useEffect(() => {
    if (!payment?.id) return;
    let alive = true;
    const i = setInterval(async () => {
      try { const r = await api.payment(payment.id); if (alive) setPoll(r.payment); } catch {}
    }, 4_000);
    return () => { alive = false; clearInterval(i); };
  }, [payment?.id]);

  const status = poll?.live_status || poll?.status || 'pending';
  const ok = status === 'succeeded';

  return (
    <div className="card p-5 space-y-3">
      <div className={`text-lg font-semibold ${ok ? 'text-emerald-700' : 'text-slate-700'}`}>
        {ok ? 'Payment successful' : flow === 'text2pay' ? 'Pay link sent — awaiting patient' : `Status: ${status}`}
      </div>
      <ul className="text-sm text-slate-600 space-y-1">
        <li>Amount: <strong>${Number(poll?.amount || 0).toFixed(2)}</strong></li>
        <li>Method: <strong>{flow === 'in_app' ? 'Card on device' : 'Text-to-pay SMS'}</strong></li>
        {poll?.processor_payment_id && <li>Processor ID: <span className="font-mono text-xs">{poll.processor_payment_id}</span></li>}
        {poll?.payment_url && (
          <li>Payment link: <a href={poll.payment_url} target="_blank" rel="noreferrer"
            className="hover:underline" style={{ color: 'var(--brand-primary)' }}>{poll.payment_url}</a></li>
        )}
        {poll?.sms_error && <li className="text-amber-700">SMS notice: {poll.sms_error}</li>}
        {poll?.consented_at && <li className="text-emerald-700">Consent captured ✓</li>}
      </ul>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={onDone}>Done</button>
        {!ok && flow === 'text2pay' && poll?.payment_url && (
          <a href={poll.payment_url} target="_blank" rel="noreferrer" className="btn-ghost">Open link</a>
        )}
      </div>
      <div className="text-xs text-slate-500">Status auto-refreshes every 4 seconds. Webhook updates land in real time.</div>
    </div>
  );
}
